import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { finalize, Subject, switchMap, take, throwError} from 'rxjs';
import { PremiseHazardType } from '@premise-hazard-type-models/premise-hazard-type.model';
import { ResponseItem } from '@models/response-item.model';
import { AdminRestService } from '@rest/admin-rest.service';
import { PremiseHazardCommandsRestService } from '@rest/premise-hazard-command-rest.service';
import { PremiseHazardQueryRestService } from '@rest/premise-hazard-rest.service';
import { UserService } from '@services/user.service';
import { BasePageComponent } from '../../shared/base-page.component';
import { ToastServiceManager } from '@app/services/toast-service-manager.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-premise-hazard-type-page',
    templateUrl: './premise-hazard-type-page.component.html',
    styleUrls: ['./premise-hazard-type-page.component.scss'],
    standalone: false
})
export class PremiseHazardTypePageComponent extends BasePageComponent<PremiseHazardType> implements OnInit, OnDestroy {
  constructor(
    private restService: PremiseHazardQueryRestService,
    private commandService: PremiseHazardCommandsRestService,
    private readonly transloco: TranslocoService,
    adminApi: AdminRestService,
    confirmationService: ConfirmationService,
    cdr: ChangeDetectorRef,
    userService: UserService,    
    private readonly toastServiceManager: ToastServiceManager) {
    super(adminApi, confirmationService, cdr, userService);
    this.AllowInteraction = true; // Initialize to true for premise hazard types (no additional data loading needed)
  }

  public openDeleteConfirmDialog(): void {
    const header = this.transloco.translate('PremiseHazardType.Page.modal.delete.header');
    const message = this.transloco.translate('PremiseHazardType.Page.modal.delete.content', { premiseHazardTypeName: this.SelectedPremiseHazardType!.code });
    this.openDeleteConfirm(header, message);
  }

  // Expose base class properties with specific names for template compatibility
  get SelectedPremiseHazardType(): PremiseHazardType | undefined {
    return this.SelectedItem;
  }
  set SelectedPremiseHazardType(value: PremiseHazardType | undefined) {
    this.SelectedItem = value;
  }

  get SelectedPremiseHazardTypeEtag(): string | undefined {
    return this.SelectedItemEtag;
  }
  set SelectedPremiseHazardTypeEtag(value: string | undefined) {
    this.SelectedItemEtag = value;
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  get isSharedHazardType(): boolean {
    return this.isShared;
  }
  set isSharedHazardType(value: boolean) {
    this.isShared = value;
  }

  get changePremiseHazardTypeViewSubject(): Subject<ResponseItem<PremiseHazardType> | undefined> {
    return this.changeViewSubject as Subject<ResponseItem<PremiseHazardType> | undefined>;
  }

  get premiseHazardTypeDeletedSubject(): Subject<PremiseHazardType> {
    return this.itemDeletedSubject as Subject<PremiseHazardType>;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  public onPremiseHazardTypeSelected(premiseHazardType: PremiseHazardType): void {
    this.selectItem(premiseHazardType);
  }

  public addNewPremiseHazardTypeClicked(): void {
    this.addNewItemClicked();
  }

  public setSharedHazardType(event: any): void {
    this.setShared(event);
  }

  // Override base class methods
  protected override deleteItem(): void {
    if (!this.SelectedPremiseHazardType) {
      return;
    }
    this.interactionDisabled();
    this.restService.getPremiseHazardType(this.SelectedPremiseHazardType.id)
      .pipe(        
        switchMap(response => {
          if(response.Item) {
            return this.commandService.deletePremiseHazardType(response.Item.id, response.ETag!);
          }
          return throwError(() => new Error('Premise hazard type not found for deletion.'));
        }),        
        finalize(() => this.interactionEnabled())
      )
      .subscribe({
        next: () => {
           this.premiseHazardTypeDeletedSubject.next(this.SelectedPremiseHazardType!);
           this.listHidden = false;
           this.IsViewing = true;
           this.IsEditing = false;
        },
        error: (error) => {        
          let errorMessage: string = 'An unexpected error occurred.'
          if (error instanceof HttpErrorResponse) {
            let httpError = error as HttpErrorResponse;
            if (httpError.status === 409) {
              const deleteFailedMessage = this.transloco.translate('PremiseHazardType.Page.deleteFailedError');
              errorMessage = deleteFailedMessage;
            }
          }  
          this.toastServiceManager.error(errorMessage, undefined, {closeButton: true});
        }
      });   
  }

  protected override loadItemForEdit(id: string): void {
    this.restService.getPremiseHazardType(id)
      .pipe(take(1))
      .subscribe(response => {
        if (response.Item) {
          this.SelectedPremiseHazardType = response.Item;
          this.SelectedPremiseHazardTypeEtag = response.ETag;
          this.IsViewing = false;
          this.IsEditing = true;
          this.interactionEnabled();
        }
      });
  }

  protected override onRefreshClicked(): void {
    // No additional refresh logic needed for premise hazard types
  }
}
